import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useTenant } from "../../Contexts/TenantContext";
import { useAuth } from "../../Contexts/AuthContext";
import { useCart, getCartItemKey } from "../../Contexts/CartContext";
import { useScrollLock } from "../../Hooks/useScrollLock";
import { cartShareService } from "../../Api/Services/cartShareService";
import { formatPriceBRL } from "../../Utils/DataFormatters";
import { FaTimes, FaShippingFast } from "react-icons/fa";
import { toast } from "react-toastify";
import CartProductItem from "../CartProductItem/CartProductItem";
import Button from "../Button/Button";

interface CartSidebarProps {
  isOpen: boolean;
  onClose: () => void;
};

const CartSidebar = ({ isOpen, onClose }: CartSidebarProps) => {
  const navigate = useNavigate();

  const { tenantPreference, tenantShippingProviderConfig } = useTenant();
  const { isAdmin, isOperator } = useAuth();
  const { cartItems, getTotal } = useCart();
  
  const [hideFreeShippingBanner, setHideFreeShippingBanner] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(isOpen);

  useScrollLock(isOpen);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (isOpen) {
      setShouldRender(true);
      setIsVisible(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsVisible(true));
      });
    } else {
      setIsVisible(false);
      timer = setTimeout(() => setShouldRender(false), 300);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isOpen]);

  if (!shouldRender) {
    return null;
  }

  const handleFinishOrder = async () => {
    const minOrder = tenantPreference?.minOrderSubtotal;
    if (minOrder && getTotal() < minOrder) {
      toast.warn(`${"Valor mínimo para finalizar a compra"}: ${formatPriceBRL(minOrder)}.`);
      return;
    }

    if (!isAdmin && !isOperator) {
      try {
        const { shortId } = await cartShareService.create(
          cartItems.map(item => ({ productId: item.id, quantity: item.quantity }))
        );
        navigate(`/carrinho?c=${shortId}`);
      } catch {
        toast.error("Não foi possível gerar o link do carrinho. Tente novamente.");
      }
    }
  };

  return (
    <>
      <div 
        className={`fixed inset-0 bg-black bg-opacity-30 z-[9998] transition-opacity duration-300 ${isVisible ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />   
      <div
        className={`fixed top-0 right-0 w-[90%] h-screen supports-[height:100dvh]:h-dvh bg-white shadow-lg z-[9999] flex flex-col transform transition-transform duration-300 ease-out ${isVisible ? "translate-x-0" : "translate-x-full"} sm:w-180 max-w-full`}
      >
        <div className={`flex justify-between items-center bg-primary-120 p-4 border-b`}>
          <h2 className={`text-lg font-bold`}>
            {"Seu Carrinho"}
          </h2>
          <button
            className={`text-primary-600 hover:text-primary-700`}
            onClick={onClose}
          >
            <FaTimes size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {cartItems.length > 0 ? (
            <div className="grid auto-rows-fr">
              {cartItems.map(item => (
                <CartProductItem
                  key={getCartItemKey(item)}
                  item={item}
                />
              ))}
            </div>
          ) : (
            <div className="p-6 space-y-3 text-sm text-gray-600">
              <p className={`text-base font-medium text-gray-700`}>
                {"Seu carrinho está vazio."}
              </p>
            </div>
          )}
        </div>
        {cartItems.length > 0 && (
          <div className={`p-4 border-t space-y-3`}>
            {tenantShippingProviderConfig?.offerFreeShipping && tenantShippingProviderConfig?.minOrderSubtotalForFreeShipping && !hideFreeShippingBanner && (
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded">
                <div className="flex items-start gap-2 flex-1 text-sm text-amber-900">
                  <FaShippingFast className="mt-1 text-amber-700" />
                  <span className="leading-relaxed">
                    {tenantShippingProviderConfig.minOrderSubtotalForFreeShipping - getTotal() <= 0
                      ? "Parabéns! Você já atingiu o valor mínimo para frete grátis."
                      : `${"Com mais"} ${formatPriceBRL(tenantShippingProviderConfig.minOrderSubtotalForFreeShipping - getTotal())} ${"o frete é por nossa conta."}`}
                  </span>
                </div>
                <button
                  className="text-amber-700 hover:text-amber-800 text-lg leading-none"
                  onClick={() => setHideFreeShippingBanner(true)}
                  aria-label={"Fechar aviso de frete grátis"}
                >
                  <FaTimes />
                </button>
              </div>
            )}

            <div className={`flex justify-between font-bold mb-2`}>
              <span>{"Total"}:</span>
              <span>{formatPriceBRL(getTotal())}</span>
            </div>
            <div className={`flex flex-col gap-2 sm:flex-row`}>
              <Button variant="secondary"
                className="w-full py-2"
                onClick={onClose}
              >
                {"Continuar comprando"}
              </Button>
              <Button 
                className="w-full py-2"
                onClick={handleFinishOrder}
              >
                {"Finalizar Compra"}
              </Button>
            </div> 
          </div>    
        )}      

        {cartItems.length === 0 && tenantShippingProviderConfig?.offerFreeShipping && tenantShippingProviderConfig?.minOrderSubtotalForFreeShipping && !hideFreeShippingBanner && (
          <div className="p-4 border-t">
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded">
              <div className="flex items-start gap-2 flex-1 text-sm text-amber-900">
                <FaShippingFast className="mt-1 text-amber-700" />
                <span className="leading-relaxed">
                  {"Com"} {formatPriceBRL(tenantShippingProviderConfig.minOrderSubtotalForFreeShipping)} {"em produtos, o frete é por nossa conta."}
                </span>
              </div>
              <button
                className="text-amber-700 hover:text-amber-800 text-lg leading-none"
                onClick={() => setHideFreeShippingBanner(true)}
                aria-label={"Fechar aviso de frete grátis"}
              >
                <FaTimes />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CartSidebar;
